import { Controller } from "@hotwired/stimulus";

/**
 * Nested Form Controller
 *
 * This controller manages the dynamic addition and removal of nested form fields.
 *
 * Requirements:
 * 1. A container element with data-nested-form-target="container"
 * 2. A template element with data-nested-form-target="template"
 * 3. Add button with data-action="nested-form#addField"
 * 4. Remove buttons with data-action="nested-form#removeField"
 * 5. Hidden _destroy inputs with data-nested-form-target="destroyInput"
 *
 * The controller allows for dynamically adding new fields and removing existing ones,
 * while properly handling the destruction of persisted records.
 *
 * Example usage:
 * <form data-controller="nested-form">
 *   <div data-nested-form-target="container">
 *     <!-- Existing fields rendered here -->
 *   </div>
 *   <button type="button" data-action="nested-form#addField">Add Field</button>
 *   <template data-nested-form-target="template">
 *     <!-- Template for new fields -->
 *   </template>
 * </form>
 */
export default class extends Controller {
  static targets = ["template", "container", "field", "destroyInput"];

  addField(event) {
    event.preventDefault();
    // The NEW_RECORD placeholder is crucial for dynamic field addition:
    // 1. It's used in the template to represent a unique identifier for new records
    // 2. We replace it with a timestamp to ensure each new field has a unique ID
    // 3. This allows Rails to correctly process new records in nested attributes
    //
    // Example:
    // - Template might have: name="modifier_group[modifiers_attributes][NEW_RECORD][name]"
    // - After replacement: name="modifier_group[modifiers_attributes][1629384755][name]"
    //
    // This approach ensures that:
    // a) Each new field has a unique identifier
    // b) Rails can distinguish between new and existing records
    // c) Multiple new fields can be added without conflicting IDs
    const content = this.templateTarget.innerHTML.replace(
      /NEW_RECORD/g,
      new Date().getTime().toString()
    );
    this.containerTarget.insertAdjacentHTML("beforeend", content);
  }

  removeField(event) {
    event.preventDefault();
    const field = event.target.closest("[data-nested-form-target='field']");

    // field.dataset.newRecord is used to distinguish between newly added fields
    // and existing records. This is crucial because:
    // 1. New records can be completely removed from the DOM
    // 2. Existing records need to be marked for destruction but kept in the form
    //    so that Rails can process their removal server-side
    if (field.dataset.newRecord === "true") {
      field.remove();
    } else {
      field.style.display = "none";
      field.querySelector("[data-nested-form-target='destroyInput']").value =
        "1";
    }
    this.disableRemovedFields(field);
  }

  // disableRemovedFields is important for several reasons:
  // 1. It prevents removed fields from interfering with form submission
  // 2. It ensures that required fields that are removed don't block form submission
  // 3. It maintains data integrity by not sending data from removed fields
  disableRemovedFields(field) {
    const inputs = field.querySelectorAll('input:not([type="hidden"])');
    inputs.forEach((input) => {
      input.disabled = true;
      input.required = false;
    });
  }
}
